/* ============================================================================
   All collections search (sections/kc-all-bento.liquid): finds collections
   as you type.

   The section prints every collection with products (name, full title,
   handle, link, product count) as JSON beside the field. Typing lists up to
   eight matches under it, best first: names that start with what was typed,
   then names with a word that starts with it, then the full title or handle,
   then anywhere in the name; ties go to the bigger collection. Accents and
   case don't matter ("pokemon" finds Pokémon). The last row always searches
   products instead.

   It's a combobox: the first row is highlighted, arrow keys move the
   highlight, Enter or the button opens it, Escape closes the list (or
   clears the field). Rows are real links, so they can be opened in a new
   tab. Without this script the form is a plain product search.
   ============================================================================ */
(() => {
  const MAX = 8;

  const fold = (text) =>
    String(text)
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase();

  const wordsOf = (text) => fold(text).split(/[^a-z0-9]+/).filter(Boolean);

  /* The name folded character by character, remembering where each folded
     character came from, so a match can be marked in the original name. */
  function foldMap(text) {
    let folded = '';
    const from = [];
    for (let i = 0; i < text.length; i += 1) {
      const part = fold(text[i]);
      for (let k = 0; k < part.length; k += 1) {
        folded += part[k];
        from.push(i);
      }
    }
    return { folded, from };
  }

  /* Ranges of the name to mark: each typed word, preferably where a word
     of the name starts. */
  function marks(name, words) {
    const { folded, from } = foldMap(name);
    const ranges = [];
    words.forEach((word) => {
      let at = -1;
      const re = new RegExp(`(^|[^a-z0-9])${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`);
      const hit = re.exec(folded);
      if (hit) at = hit.index + hit[1].length;
      else at = folded.indexOf(word);
      if (at < 0) return;
      ranges.push([from[at], from[at + word.length - 1] + 1]);
    });
    ranges.sort((a, b) => a[0] - b[0]);
    return ranges.reduce((out, range) => {
      const last = out[out.length - 1];
      if (last && range[0] <= last[1]) last[1] = Math.max(last[1], range[1]);
      else out.push(range);
      return out;
    }, []);
  }

  function fillMarked(el, name, words) {
    let pos = 0;
    marks(name, words).forEach(([start, end]) => {
      if (start > pos) el.append(name.slice(pos, start));
      const mark = document.createElement('mark');
      mark.textContent = name.slice(start, end);
      el.append(mark);
      pos = end;
    });
    if (pos < name.length) el.append(name.slice(pos));
  }

  class Finder {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('form');
      this.input = root.querySelector('[role="combobox"]');
      this.drop = root.querySelector('.kc-ab__drop');
      this.none = root.querySelector('.kc-ab__none');
      this.list = root.querySelector('[role="listbox"]');
      this.status = root.querySelector('[role="status"]');
      this.items = null;
      this.options = [];
      this.active = -1;
      this.query = '';

      this.input.addEventListener('input', () => this.update());
      this.input.addEventListener('focus', () => {
        if (this.input.value.trim()) this.update();
      });
      this.input.addEventListener('keydown', (event) => this.onKey(event));
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.list.addEventListener('pointermove', (event) => {
        const index = this.options.indexOf(event.target.closest('[role="option"]'));
        if (index >= 0 && index !== this.active) this.setActive(index);
      });
      /* Focus stays in the field while a row is pressed (Safari doesn't
         focus links on click, which would close the list before the
         click lands). */
      this.drop.addEventListener('mousedown', (event) => event.preventDefault());
      root.addEventListener('focusout', (event) => {
        if (!root.contains(event.relatedTarget)) this.close();
      });
      document.addEventListener('pointerdown', (event) => {
        if (!root.contains(event.target)) this.close();
      });
      window.addEventListener('pageshow', () => this.close());
    }

    load() {
      if (this.items) return this.items;
      let data = [];
      try {
        data = JSON.parse(this.root.querySelector('[data-kc-finder-data]').textContent);
      } catch (error) {
        data = [];
      }
      this.items = data.map((item) => ({
        ...item,
        nameFolded: fold(item.n),
        nameWords: wordsOf(item.n),
        moreFolded: fold(`${item.t} ${item.h.replace(/-/g, ' ')}`),
        moreWords: wordsOf(`${item.t} ${item.h}`)
      }));
      return this.items;
    }

    match(query) {
      const folded = fold(query).trim().replace(/\s+/g, ' ');
      const words = wordsOf(query);
      if (!words.length) return [];
      const starts = (list) => words.every((word) => list.some((w) => w.startsWith(word)));
      const found = [];
      this.load().forEach((item) => {
        let rank = -1;
        if (item.nameFolded.startsWith(folded)) rank = 0;
        else if (starts(item.nameWords)) rank = 1;
        else if (starts(item.moreWords)) rank = 2;
        else if (item.nameFolded.includes(folded)) rank = 3;
        else if (item.moreFolded.includes(folded)) rank = 4;
        if (rank >= 0) found.push({ rank, item });
      });
      found.sort((a, b) => a.rank - b.rank || b.item.c - a.item.c || a.item.n.localeCompare(b.item.n));
      return found.slice(0, MAX).map((entry) => entry.item);
    }

    update() {
      const query = this.input.value.trim();
      this.query = query;
      if (!query) {
        this.close();
        this.announce('');
        return;
      }

      const words = wordsOf(query);
      const found = this.match(query);
      const id = this.list.id;
      this.list.textContent = '';
      this.options = found.map((item, i) => {
        const option = document.createElement('a');
        option.className = 'kc-ab__opt';
        option.id = `${id}-${i}`;
        option.href = item.u;
        option.tabIndex = -1;
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');
        const name = document.createElement('span');
        name.className = 'kc-ab__opt-name';
        fillMarked(name, item.n, words);
        const count = document.createElement('span');
        count.className = 'kc-ab__opt-n';
        count.textContent = Number(item.c).toLocaleString('en-US');
        option.append(name, count);
        return option;
      });

      const all = document.createElement('a');
      all.className = 'kc-ab__opt kc-ab__opt--all';
      all.id = `${id}-all`;
      all.href = `${this.root.dataset.searchUrl || '/search'}?q=${encodeURIComponent(query)}&options%5Bprefix%5D=last`;
      all.tabIndex = -1;
      all.setAttribute('role', 'option');
      all.setAttribute('aria-selected', 'false');
      all.innerHTML =
        '<svg class="kc-ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.6-3.6"/></svg>';
      const allText = document.createElement('span');
      allText.className = 'kc-ab__opt-name';
      allText.textContent = (this.root.dataset.allText || '[q]').replace('[q]', query);
      all.append(allText);
      this.options.push(all);
      this.list.append(...this.options);

      this.none.hidden = found.length > 0;
      if (!found.length) this.none.textContent = (this.root.dataset.noneText || '').replace('[q]', query);

      this.open();
      this.setActive(0);
      clearTimeout(this.announceTimer);
      this.announceTimer = setTimeout(() => {
        const one = this.root.dataset.foundOne || '1';
        const other = this.root.dataset.foundOther || '[count]';
        this.announce(found.length === 1 ? one : other.replace('[count]', found.length));
      }, 400);
    }

    announce(text) {
      if (this.status) this.status.textContent = text;
    }

    open() {
      this.drop.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.drop.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.active = -1;
    }

    setActive(index, reveal) {
      if (!this.options.length) return;
      const count = this.options.length;
      this.active = ((index % count) + count) % count;
      this.options.forEach((option, i) => option.setAttribute('aria-selected', String(i === this.active)));
      const option = this.options[this.active];
      this.input.setAttribute('aria-activedescendant', option.id);
      if (reveal) option.scrollIntoView({ block: 'nearest' });
    }

    go() {
      const option = this.options[this.active] || this.options[0];
      if (option) window.location.href = option.href;
    }

    onKey(event) {
      const isOpen = !this.drop.hidden;
      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp':
          event.preventDefault();
          if (!isOpen) {
            this.update();
            return;
          }
          this.setActive(this.active + (event.key === 'ArrowDown' ? 1 : -1), true);
          break;
        case 'Escape':
          if (isOpen) {
            event.preventDefault();
            this.close();
          } else if (this.input.value) {
            event.preventDefault();
            this.input.value = '';
            this.announce('');
          }
          break;
        default:
          break;
      }
    }

    onSubmit(event) {
      if (!this.input.value.trim()) {
        event.preventDefault();
        this.input.focus();
        return;
      }
      if (this.drop.hidden || this.query !== this.input.value.trim()) this.update();
      if (!this.options.length) return;
      event.preventDefault();
      this.go();
    }
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-kc-finder]').forEach((root) => {
      if (!root.kcFinder) root.kcFinder = new Finder(root);
    });
  }

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
